import { formatMoney } from "@justhome/common";
import { color, font } from "@lib/theme";
import { Switch } from "@justhome/react-ui";
import { CategoryScale, Chart, ChartData, ChartDataset, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { FC, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { calculateRepayment, CompleteRepaymentPlan, Data, RepaymentPlan } from "./calculateRepayment";
import { RepaymentRateChange } from "./types";
Chart.register(LineElement, CategoryScale, LinearScale, PointElement, Filler, Tooltip, AnnotationPlugin, Legend);
function createDiagonalPattern(color: string) {
  const shape = document.createElement("canvas");
  shape.width = 16;
  shape.height = 16;
  const ctx = shape.getContext("2d")!;
  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.moveTo(16, 0);
  ctx.lineTo(0, 16);
  ctx.stroke();
  return ctx.createPattern(shape, "repeat");
}
interface RepaymentChartProps {
  repaymentPlan?: ReturnType<typeof calculateRepayment>;
  repaymentPlanSecondVariant?: ReturnType<typeof calculateRepayment>;
  isNotDefault?: boolean;
}
function getChartData(repaymentPlan: ReturnType<typeof calculateRepayment>, fill: boolean, secondVariant: boolean, suffix: string): ChartDataset<"line", Data[]>[] {
  const interestColor = secondVariant ? color("info700") : color("primary800");
  const fillColor = secondVariant ? color("info700", 0.2) : color("primary800", 0.2);
  return [
  // Extra empty data to add legend entry for repayment rate changes
  {
    type: "line",
    label: `Tilgungswechsel ${suffix}`,
    backgroundColor: interestColor,
    borderColor: interestColor,
    hidden: repaymentPlan.values.repaymentRateChange.length < 1,
    data: []
  }, {
    label: `Zinskosten ${suffix}`,
    parsing: {
      xAxisKey: "x",
      yAxisKey: "interest"
    },
    pointRadius: 0,
    borderColor: interestColor,
    borderDash: [8, 8],
    data: repaymentPlan.repayment.data
  }, {
    label: `Restschuld ${suffix}`,
    fill: fill,
    pointRadius: 0,
    backgroundColor: fillColor,
    borderColor: interestColor,
    data: repaymentPlan.repayment.data
  }];
}
function addSecondaryRepaymentPlan(chartData: ChartData<"line", Data[], unknown>, secondaryRepayment: RepaymentPlan, fill: boolean, secondVariant: boolean, suffix: string) {
  const borderColor = secondVariant ? color("info700") : color("primary800");
  const fillColor = secondVariant ? color("info700", 0.2) : color("primary800", 0.6);
  chartData.datasets.push({
    label: `Zinskosten ${suffix}`,
    parsing: {
      xAxisKey: "x",
      yAxisKey: "interest"
    },
    pointRadius: 0,
    borderColor: borderColor,
    borderDash: [8, 8],
    data: secondaryRepayment.data
  });
  chartData.datasets.push({
    label: `Restschuld ${suffix}`,
    fill: fill,
    pointRadius: 0,
    backgroundColor: createDiagonalPattern(fillColor) as any,
    borderColor: borderColor,
    data: secondaryRepayment.data
  });
}
function setRepaymentChangePoint(repaymentRateChange: RepaymentRateChange, startYear: number, repaymentPlan: CompleteRepaymentPlan, acc: {
  [x: string]: {
    type: string;
    xValue: number;
    yValue: number;
    backgroundColor: string;
    borderColor: string;
    radius: number;
  };
}, i: number, color: string) {
  const xValue = repaymentRateChange.year - startYear;
  let yValue: number | undefined = undefined;
  if (repaymentPlan.repayment.data[xValue]) {
    yValue = repaymentPlan.repayment.data[xValue].y;
  }
  if (yValue) {
    acc[`point${i}`] = {
      type: "point",
      xValue,
      yValue,
      backgroundColor: color,
      borderColor: color,
      radius: 4
    };
  }
  return acc;
}
const RepaymentChart: FC<RepaymentChartProps> = ({
  repaymentPlan,
  repaymentPlanSecondVariant,
  isNotDefault
}) => {
  const [variant1Enabled, setVariant1Enabled] = useState<boolean>(true);
  const [variant2Enabled, setVariant2Enabled] = useState<boolean>(!!isNotDefault);
  const data = useMemo(() => {
    if (!repaymentPlan) {
      return {
        datasets: []
      };
    }
    const chartData: ChartData<"line", Data[], unknown> = {
      datasets: []
    };
    if (variant1Enabled) {
      chartData.datasets.push(...getChartData(repaymentPlan, variant1Enabled !== variant2Enabled, false, "V1"));
    }
    if (repaymentPlanSecondVariant && variant2Enabled) {
      chartData.datasets.push(...getChartData(repaymentPlanSecondVariant, variant1Enabled !== variant2Enabled, true, "V2"));
    }

    // Split adding secondary repayment datasets to filter in the legend by datasetIndex to prevent duplicates
    if (variant1Enabled && repaymentPlan.secondaryRepayment) {
      addSecondaryRepaymentPlan(chartData, repaymentPlan.secondaryRepayment, variant1Enabled !== variant2Enabled, false, "V1");
    }
    if (variant2Enabled && repaymentPlanSecondVariant?.secondaryRepayment) {
      addSecondaryRepaymentPlan(chartData, repaymentPlanSecondVariant.secondaryRepayment, variant1Enabled !== variant2Enabled, true, "V2");
    }
    return chartData;
  }, [repaymentPlan, repaymentPlanSecondVariant, variant1Enabled, variant2Enabled]);
  const maxX = Math.max(20, Math.floor((repaymentPlan?.totalDurationYears || 0) / 10) * 10 + 10, Math.floor((repaymentPlanSecondVariant?.totalDurationYears || 0) / 10) * 10 + 10);
  const startYear = repaymentPlan?.repayment.startDate.getFullYear();
  let points = 0;
  const repaymentChangePoints = variant1Enabled && startYear && repaymentPlan?.values.repaymentRateChange.reduce((acc, repaymentRateChange, i) => {
    points++;
    return setRepaymentChangePoint(repaymentRateChange, startYear, repaymentPlan, acc, i, color("primary800"));
  }, {});
  const startYearSecondVariant = repaymentPlanSecondVariant?.repayment.startDate.getFullYear();
  const repaymentChangePointsSecondVariant = variant2Enabled && startYearSecondVariant && repaymentPlanSecondVariant?.values.repaymentRateChange.reduce((acc_0, repaymentRateChange_0, i_0) => {
    return setRepaymentChangePoint(repaymentRateChange_0, startYearSecondVariant, repaymentPlanSecondVariant, acc_0, i_0 + points, color("info700"));
  }, {});
  return <>
      <div className="flex gap-s mb-s">
        <Switch checked={variant1Enabled} onChange={enabled => {
        setVariant1Enabled(enabled);
        if (!enabled && !variant2Enabled) {
          setVariant2Enabled(true);
        }
      }} label="Variante 1"></Switch>
        <Switch checked={variant2Enabled} onChange={enabled_0 => {
        setVariant2Enabled(enabled_0);
        if (!enabled_0 && !variant1Enabled) {
          setVariant1Enabled(true);
        }
      }} label="Variante 2"></Switch>
      </div>
      <Line options={{
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 0
      },
      font: {
        family: font.primary
      },
      interaction: {
        mode: "index",
        intersect: false
      },
      scales: {
        y: {
          type: "linear",
          display: true,
          position: "left",
          min: 0,
          ticks: {
            callback: value => formatMoney(Number(value), false, "de")
          }
        },
        x: {
          ticks: {
            autoSkipPadding: 20,
            callback: value_0 => value_0 ? `${value_0} Jahre` : ""
          },
          grid: {
            display: false
          },
          type: "linear",
          display: true,
          max: maxX
        }
      },
      plugins: {
        legend: {
          position: "top",
          align: "end",
          onClick() {},
          labels: {
            useBorderRadius: true,
            borderRadius: 6,
            boxWidth: 12,
            boxHeight: 12,
            filter(item) {
              return (variant1Enabled && variant2Enabled ? item.datasetIndex! < 6 : item.datasetIndex! < 3) && !item.hidden;
            }
          }
        },
        tooltip: {
          backgroundColor: color("neutral0"),
          titleColor: color("neutral675"),
          bodyColor: color("neutral675"),
          borderColor: color("neutral50"),
          borderWidth: 1,
          position: "nearest",
          yAlign: "center",
          titleFont: {
            size: 16,
            weight: 400
          },
          bodyFont: {
            size: 16
          },
          displayColors: false,
          cornerRadius: 8,
          itemSort(a, b) {
            return a.datasetIndex - b.datasetIndex;
          },
          filter(current, index, array) {
            const firstIndex = array.findIndex(value_1 => value_1.dataset.label === current.dataset.label);
            return index === firstIndex;
          },
          callbacks: {
            labelColor(context) {
              const color = context.dataset.borderColor as string;
              return {
                borderColor: color,
                backgroundColor: color,
                borderWidth: 0,
                borderRadius: 0
              };
            },
            labelTextColor: function (context_0) {
              return context_0.dataset.borderColor as string;
            },
            title(items) {
              const item_0 = items.sort((a_0, b_0) => (a_0.raw as Data).date.getFullYear() - (b_0.raw as Data).date.getFullYear())[0];
              if (!item_0) return "";
              const year = (item_0.raw as Data).date.getFullYear();
              return `${Math.floor(item_0.parsed.x)} Jahre, ${year}`;
            },
            label(context_1) {
              return `${context_1.dataset.label}: ${formatMoney(context_1.parsed.y, false, "de")}`;
            }
          }
        },
        annotation: {
          annotations: {
            ...repaymentChangePoints,
            ...repaymentChangePointsSecondVariant,
            line1: variant1Enabled && !variant2Enabled ? {
              type: "line",
              xMin: repaymentPlan?.values.fixedInterestPeriodYears,
              xMax: repaymentPlan?.values.fixedInterestPeriodYears,
              borderDash: [8, 8],
              borderColor: color("primary800", 0.6),
              borderWidth: 2
            } : undefined,
            line2: variant2Enabled && !variant1Enabled ? {
              type: "line",
              xMin: repaymentPlanSecondVariant?.values.fixedInterestPeriodYears,
              xMax: repaymentPlanSecondVariant?.values.fixedInterestPeriodYears,
              borderDash: [8, 8],
              borderColor: color("info700", 0.6),
              borderWidth: 2
            } : undefined
          }
        }
      }
    }} data={data} />
    </>;
};
export default RepaymentChart;